import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import WorkCard from "@/components/feature/WorkCard";
import Pagination from "@/components/base/Pagination";
import { categories } from "@/mocks/categories";
import { works } from "@/mocks/works";
import { useSeo } from "@/hooks/useSeo";
import type { Work } from "@/types";

const PAGE_SIZE = 9;
type SortKey = "latest" | "hot" | "recommend";

export default function CategoryPage() {
  const { categoryId } = useParams();
  const category = categories.find((c) => c.id === categoryId) || categories[0];

  const [tags, setTags] = useState<string[]>([]);
  const [year, setYear] = useState<string>("all");
  const [author, setAuthor] = useState<string>("all");
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useSeo({
    title: `${category.name} - 女性ins大片摄影作品`,
    description: `浏览${category.name}分类下的女性INS风格摄影作品。${category.description}`,
    keywords: `${category.name},女性ins大片,${category.name}摄影,女性摄影作品,INS风格摄影`,
    path: `/category/${category.id}`,
  });

  const categoryWorks = useMemo(
    () => (works as Work[]).filter((w) => w.categoryId === category.id),
    [category.id]
  );

  const allTags = useMemo(() => {
    const set = new Set<string>();
    categoryWorks.forEach((w) => w.tags.forEach((t) => set.add(t)));
    return Array.from(set);
  }, [categoryWorks]);

  const allYears = useMemo(() => {
    const set = new Set<string>();
    categoryWorks.forEach((w) => set.add(w.date.slice(0, 4)));
    return Array.from(set).sort((a, b) => (a < b ? 1 : -1));
  }, [categoryWorks]);

  const allAuthors = useMemo(() => {
    const set = new Set<string>();
    categoryWorks.forEach((w) => set.add(w.author));
    return Array.from(set);
  }, [categoryWorks]);

  const filtered = useMemo(() => {
    let list = categoryWorks.filter((w) => {
      const tagOk = tags.length === 0 || tags.some((t) => w.tags.includes(t));
      const yearOk = year === "all" || w.date.startsWith(year);
      const authorOk = author === "all" || w.author === author;
      return tagOk && yearOk && authorOk;
    });
    if (sort === "latest") list = list.sort((a, b) => (a.date < b.date ? 1 : -1));
    if (sort === "hot") list = list.sort((a, b) => b.views - a.views);
    if (sort === "recommend")
      list = list.sort((a, b) => Number(b.featured) - Number(a.featured) || b.views - a.views);
    return list;
  }, [categoryWorks, tags, year, author, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const toggleTag = (t: string) => {
    setPage(1);
    setTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  const clearAll = () => {
    setTags([]);
    setYear("all");
    setAuthor("all");
    setSort("latest");
    setPage(1);
  };

  const hasFilter = tags.length > 0 || year !== "all" || author !== "all";

  const filterPanel = (
    <div className="space-y-6">
      <div>
        <p className="font-label text-xs font-medium text-foreground-500 mb-3">风格标签</p>
        <div className="flex flex-wrap gap-2">
          {allTags.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => toggleTag(t)}
              className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                tags.includes(t)
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="font-label text-xs font-medium text-foreground-500 mb-3">拍摄年份</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              setYear("all");
              setPage(1);
            }}
            className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
              year === "all"
                ? "bg-secondary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-secondary-100"
            }`}
          >
            全部年份
          </button>
          {allYears.map((y) => (
            <button
              key={y}
              type="button"
              onClick={() => {
                setYear(y);
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                year === y
                  ? "bg-secondary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-secondary-100"
              }`}
            >
              {y}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="font-label text-xs font-medium text-foreground-500 mb-3">摄影师</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              setAuthor("all");
              setPage(1);
            }}
            className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
              author === "all"
                ? "bg-accent-500 text-foreground-950"
                : "bg-background-100 text-foreground-700 hover:bg-accent-100"
            }`}
          >
            全部
          </button>
          {allAuthors.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => {
                setAuthor(a);
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                author === a
                  ? "bg-accent-500 text-foreground-950"
                  : "bg-background-100 text-foreground-700 hover:bg-accent-100"
              }`}
            >
              {a}
            </button>
          ))}
        </div>
      </div>

      {hasFilter && (
        <button
          type="button"
          onClick={clearAll}
          className="w-full py-2.5 rounded-md border border-background-300 text-sm text-foreground-600 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line mr-1"></i>
          清空筛选
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background-50">
      <SiteNavbar />
      <main className="pt-16 md:pt-[72px]">
        {/* Header */}
        <section className="bg-secondary-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-14">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4" aria-label="面包屑">
              <Link to="/" className="hover:text-primary-600 cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">{category.name}</span>
            </nav>
            <div className="flex items-center gap-4">
              <span className="w-14 h-14 rounded-full bg-primary-500 flex items-center justify-center shrink-0">
                <i className={`${category.icon} text-background-50 text-2xl`}></i>
              </span>
              <div>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1 max-w-2xl">{category.description}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar filters (desktop) */}
            <aside className="hidden lg:block w-64 shrink-0">
              <div className="sticky top-24 bg-background-50 rounded-lg border border-background-200/70 p-5">
                <h2 className="font-heading text-base font-semibold text-foreground-900 mb-5 flex items-center gap-2">
                  <i className="ri-filter-3-line text-primary-500"></i>
                  筛选作品
                </h2>
                {filterPanel}
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              {/* Toolbar */}
              <div className="flex items-center justify-between gap-3 mb-6">
                <p className="text-sm text-foreground-500">
                  共 <span className="text-primary-600 font-semibold">{filtered.length}</span> 件作品
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFilterOpen(true)}
                    className="lg:hidden px-3 h-9 rounded-md border border-background-300 text-sm text-foreground-700 flex items-center gap-1 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-filter-3-line"></i>
                    筛选
                  </button>
                  <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                    {[
                      { key: "latest", label: "最新" },
                      { key: "hot", label: "最热" },
                      { key: "recommend", label: "推荐" },
                    ].map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => {
                          setSort(s.key as SortKey);
                          setPage(1);
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                          sort === s.key
                            ? "bg-primary-500 text-background-50"
                            : "text-foreground-600 hover:text-primary-600"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {paged.length === 0 ? (
                <div className="py-20 text-center">
                  <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                  </span>
                  <p className="text-foreground-600 mb-5">未找到符合条件的作品</p>
                  <button
                    type="button"
                    onClick={clearAll}
                    className="px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    清空筛选
                  </button>
                </div>
              ) : (
                <div
                  data-product-shop
                  className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
                >
                  {paged.map((work, i) => (
                    <WorkCard key={work.id} work={work} index={i} />
                  ))}
                </div>
              )}

              <Pagination
                current={safePage}
                total={filtered.length}
                pageSize={PAGE_SIZE}
                onChange={(p) => {
                  setPage(p);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            </div>
          </div>
        </section>
      </main>

      {/* Mobile filter drawer */}
      {filterOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setFilterOpen(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-background-50 rounded-t-2xl max-h-[80vh] overflow-y-auto p-5 animate-fade-up">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-lg font-semibold text-foreground-900">筛选作品</h2>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-lg text-foreground-700"></i>
              </button>
            </div>
            {filterPanel}
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="w-full mt-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              查看 {filtered.length} 件作品
            </button>
          </div>
        </div>
      )}

      <SiteFooter />
    </div>
  );
}